import Button from "../../../../button";
import React, {useEffect, useRef, useState} from "react";
import SlideOverHeader from "../../../component/header";
import SlideOverBody from "../../../component/body";
import SlideOverFooter from "../../../component/footer";
import useSlideOver from "../../../useSlideOver";
import ApiDatabase from "server";
import {toast} from "react-toastify";
import RichTextEditor from "../../../../RichTextEditor/RichTextEditor";
import StyledInput from "../../../../styledInput/StyledInput";
import UseContext from "../../../../../hooks/useContext";
import useSlideOverBaseline from "../../../useSlideOverBaseline";
import {BanIcon, CheckIcon, SparklesIcon, WarningCircle} from "../../../../../assets/icons";
import QualificationTasksTab from "./QualificationTasksTab";
import {buildTasksPayload, toFormTasks} from "./qualificationTasks";
import {qualificationText} from "./qualificationTexts";
import {
    AI_FIELDS,
    AI_STATUS,
    AI_SUGGEST_CLIENT_TIMEOUT_MS,
    AI_SUGGEST_POLL_INTERVAL_MS,
    AI_TITLE_MAX_LENGTH,
    AI_TITLE_MIN_LENGTH,
    buildRegenerateKeep,
    getAlternativeNameOffer,
    isFieldProposed,
    isValidAiTitle,
    listAiFields,
    mergeSuggestedTasks,
    noAiFields,
    normalizeRomeInput,
    toComparableText,
    toSuggestedValues,
} from "./qualificationAiSuggestion";
import AiSuggestedMark from "./AiSuggestedMark";
import QualificationRiskTab from "./QualificationRiskTab";
import QualificationRequirementsTab from "./QualificationRequirementsTab";
import useQualificationReferentials from "./useQualificationReferentials";
import {
    buildConditionsPayload,
    buildNamesPayload,
    emptyRisk,
    isConditionsValue,
    isRiskFactorMissing,
    readInvalidName,
    removeRequirementItem,
    toFormItems,
    toFormRisk,
    toggleRequirementItem,
    toggleRiskFactor,
    toSuggestedItems,
    toSuggestedRisk,
    withPositionAtRisk,
} from "./qualificationRequirements";
import {
    AiFailureMessage,
    AiFieldPills,
    AiGenerationSteps,
    AiPrefilledBanner,
    LowConfidenceAlert,
    RegeneratedFieldChange,
    RegenerateWithAiButton,
    RegenerationSummary,
    SimilarQualificationsAlert,
} from "./QualificationAiParts";
import {diffRegeneration, REGENERATED_FIELDS, toRegenerationBaseline, toStoredValues} from "./qualificationRegeneration";
import QualificationFormSkeleton, {QualificationHeaderSkeleton} from "./QualificationFormSkeleton";

const text = (key, values) => qualificationText(`slideOver.${key}`, values)
const aiText = (key, values) => qualificationText(`ai.${key}`, values)
const regenerateText = (key, values) => qualificationText(`regenerate.${key}`, values)

// Same sectors as the api's MODEL_MAP: the AI may propose any of them, and a
// value missing here would show an empty select while still being sent.
const SECTOR_KEYS = ['sale', 'logistic', 'food', 'largeDistribution', 'administration', 'industry', 'other']

const TAB_INFORMATIONS = 'informations'
const TAB_RISK = 'risk'
const TAB_REQUIREMENTS = 'requirements'
const TAB_TASKS = 'tasks'

// Form subtitle per tab, creation included once past the first step.
const TAB_SUBTITLE_KEYS = {
    [TAB_INFORMATIONS]: 'editSubtitle',
    [TAB_RISK]: 'editRiskSubtitle',
    [TAB_REQUIREMENTS]: 'editRequirementsSubtitle',
    [TAB_TASKS]: 'editTasksSubtitle',
}

const REQUIREMENT_AI_FIELDS = ['conditions', 'tools', 'licences']

// How the before/after blocks and the regeneration summary name each field.
const REGENERATED_FIELD_LABEL_KEYS = {
    description: 'slideOver.description',
    rome: 'slideOver.rome',
    pcs: 'slideOver.pcs',
    sector: 'slideOver.sector',
    tasks: 'slideOver.tabTasks',
    conditions: 'slideOver.tabRisk',
    tools: 'requirements.tools',
    licences: 'requirements.licences',
}

const regeneratedFieldLabel = (field) => qualificationText(REGENERATED_FIELD_LABEL_KEYS[field])

// Creation goes through a first step (title, AI or not), an optional
// generation screen, then the form; modification opens the form directly.
const STAGE_START = 'start'
const STAGE_GENERATING = 'generating'
const STAGE_FORM = 'form'

// Refusals added with the qualification tasks come as { error, errorCode };
// the older ones keep their { message }.
const API_ERROR_TEXTS = {
    INVALID_TASKS: {key: 'errorInvalidTasks', tab: TAB_TASKS},
    QUALIFICATION_NAME_IMMUTABLE: {key: 'errorNameImmutable', tab: TAB_INFORMATIONS},
    INVALID_PCS_CODE: {key: 'errorInvalidPcs', tab: TAB_INFORMATIONS},
    INVALID_ROME_CODE: {key: 'errorInvalidRome', tab: TAB_INFORMATIONS},
    INVALID_AI_SUGGESTION: {key: 'errorInvalidAiSuggestion', tab: TAB_INFORMATIONS},
    QUALIFICATION_RISK_FACTOR_REQUIRED: {key: 'errorRiskFactorRequired', tab: TAB_RISK},
    // These name the refused value: `<key>` with it, `<key>Generic` if the answer has none.
    QUALIFICATION_RISK_FACTOR_UNKNOWN: {key: 'errorRiskFactorUnknown', tab: TAB_RISK, withName: true},
    QUALIFICATION_TOOL_UNKNOWN: {key: 'errorToolUnknown', tab: TAB_REQUIREMENTS, withName: true},
    QUALIFICATION_LICENCE_UNKNOWN: {key: 'errorLicenceUnknown', tab: TAB_REQUIREMENTS, withName: true},
}

const API_WARNING_TEXTS = {
    PCS_NOT_IN_PCS_ESE_2017: 'warningPcsNotInList',
}

const stopPollingSession = (polling) => {
    polling.session += 1
    clearTimeout(polling.pollTimer)
    clearTimeout(polling.deadlineTimer)
    polling.pollTimer = null
    polling.deadlineTimer = null
}

const sectorLabel = (sectorKey) => text(`sectors.${sectorKey}`) || sectorKey

// Module-level on purpose: StyledInput resets its options whenever `list`
// changes identity, so a list rebuilt at each render would loop.
const SECTOR_OPTIONS = [
    {value: '', label: text('sectorPlaceholder')},
    ...SECTOR_KEYS.map((sectorKey) => ({value: sectorKey, label: text(`sectors.${sectorKey}`)})),
]

// Aligns StyledInput's checkbox on the first line of a multi-line label.
const CHECKBOX_WRAPPER_CLASS = '[&_input]:self-start [&_input]:mt-0.5'

// Rendered inside StyledInput's flex label row: the mark goes last and to the
// right, after the optional "- Optionnel" StyledInput appends.
const FieldLabel = ({children, isAi}) => (
    <>
        <span>{children}</span>
        {isAi && <AiSuggestedMark align={'end'} className={'order-last ml-auto'}/>}
    </>
)

// Also wraps the rich text editor, which StyledInput does not render: same hint style and gap as StyledInput.
const WithHint = ({hint, children}) => (
    <div className={'flex flex-col gap-1.5'}>
        {children}
        {hint && <div className={'text-xs font-normal text-faint'}>{hint}</div>}
    </div>
)

const SlideOverQualification = () => {
    const {toggleSlideOver} = useSlideOver();
    const {
        refreshQualifications,
        setRefreshQualifications,
        refreshQualification,
        setRefreshQualification,
        objID,
        setObjID,
        setSlideOverHasUnsavedChanges,
    } = UseContext()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [initialDescription, setInitialDescription] = useState('')
    const [pcs, setPcs] = useState('')
    const [rome, setRome] = useState('')
    const [type, setType] = useState('')
    const [isToDisplayInMissionPreferences, setIsToDisplayInMissionPreferences] = useState(false)
    const [isActive, setIsActive] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [tasks, setTasks] = useState([])
    const [risk, setRisk] = useState(emptyRisk)
    const [tools, setTools] = useState([])
    const [licences, setLicences] = useState([])
    const [activeTab, setActiveTab] = useState(TAB_INFORMATIONS)
    const [submitError, setSubmitError] = useState('')
    const [stage, setStage] = useState(STAGE_START)
    const [isAiAvailable, setIsAiAvailable] = useState(false)
    const [useAi, setUseAi] = useState(false)
    const [aiTitle, setAiTitle] = useState('')
    const [aiTitleError, setAiTitleError] = useState('')
    const [generationStatus, setGenerationStatus] = useState(AI_STATUS.PENDING)
    // The classified answer, for the values the finished steps show.
    const [generationProgress, setGenerationProgress] = useState(null)
    const [aiFailed, setAiFailed] = useState(false)
    // The last `done` suggestion applied: id, confidences, alternatives, similar qualifications.
    const [suggestion, setSuggestion] = useState(null)
    // Which fields still hold the AI value as proposed.
    const [aiFields, setAiFields] = useState(noAiFields)
    const [romeLabel, setRomeLabel] = useState('')
    const [pcsLabel, setPcsLabel] = useState('')
    // Modification only: false until getQualificationByID first answers, so a
    // later reload (after saving) never brings the skeleton back.
    const [hasLoadedQualification, setHasLoadedQualification] = useState(false)
    // Modification only: fields the last regenerations replaced, { before, after, isRestored }.
    const [regeneratedFields, setRegeneratedFields] = useState({})
    // Modification only: fields listed after the last regeneration, null before any.
    const [regenerationSummary, setRegenerationSummary] = useState(null)
    // The editor only reloads on a new `initialContent`; a toggle back to the
    // same text needs a remount.
    const [descriptionEditorKey, setDescriptionEditorKey] = useState(0)
    // A field changed since the panel opened (or since the qualification loaded).
    const [hasFormChanges, setHasFormChanges] = useState(false)
    const token = localStorage.getItem('xsrfToken')

    // A new session invalidates every callback of the previous one: answers
    // arriving after a stop (close, failure, regeneration) are dropped.
    const pollingRef = useRef({session: 0, pollTimer: null, deadlineTimer: null})
    // What the editor first reports for the AI description; later reports that
    // differ from it are edits by the admin.
    const aiDescriptionRef = useRef({html: '', baseline: null})
    // Risk and requirements as they were before a generation: conditions are
    // applied from `classified`, and a failed or cancelled run must not leave them.
    const requirementsSnapshotRef = useRef(null)
    // Modification: the values as loaded from the database; a regeneration
    // reuses their task ids so saving does not archive and recreate them.
    const storedValuesRef = useRef(null)
    const {riskyPositions, toolOptions, licenceOptions} = useQualificationReferentials()

    const isCreation = !objID
    // Modification opens on the form and only leaves it while regenerating.
    const currentStage = isCreation || stage === STAGE_GENERATING ? stage : STAGE_FORM
    const isFirstLoadPending = !isCreation && !hasLoadedQualification

    const stopPolling = () => stopPollingSession(pollingRef.current)

    // The description counts by its text: the editor rewrites the stored HTML
    // when it mounts, which is no change by the admin.
    useSlideOverBaseline({
        getSnapshot: () => ({
            name,
            description: toComparableText(description).trim(),
            pcs,
            rome,
            type,
            isToDisplayInMissionPreferences,
            tasks: buildTasksPayload(tasks),
            risk: {positionAtRisk: risk.positionAtRisk, penibilite: risk.penibilite},
            tools: buildNamesPayload(tools),
            licences: buildNamesPayload(licences),
        }),
        deps: [name, description, pcs, rome, type, isToDisplayInMissionPreferences, tasks, risk, tools, licences],
        setSlideOverHasUnsavedChanges: setHasFormChanges,
        isReady: isCreation || hasLoadedQualification,
    })

    // Read by SlideOverStructure to confirm a click outside the panel. A
    // generation under way or a suggestion not yet saved would be lost too.
    const hasUnsavedChanges =
        currentStage === STAGE_GENERATING || (isCreation && Boolean(suggestion)) || hasFormChanges

    useEffect(() => {
        setSlideOverHasUnsavedChanges(hasUnsavedChanges)
    }, [hasUnsavedChanges, setSlideOverHasUnsavedChanges])

    useEffect(() => () => setSlideOverHasUnsavedChanges(false), [setSlideOverHasUnsavedChanges])

    useEffect(() => {
        const polling = pollingRef.current
        return () => stopPollingSession(polling)
    }, [])

    useEffect(() => {
        ApiDatabase.getQualificationAiSuggestStatus({}, (data) => {
            const isEnabled = data?.enabled === true
            setIsAiAvailable(isEnabled)
            if (!objID) setUseAi(isEnabled)
        }, () => {
            setIsAiAvailable(false)
            setUseAi(false)
        })
    }, [objID]);

    const markFieldEdited = (field) => {
        setAiFields((fields) => (fields[field] ? {...fields, [field]: false} : fields))
    }

    const riskFactorLabel = (besttField) =>
        riskyPositions.items.find((position) => position.besttField === besttField)?.name ?? besttField

    const apiErrorText = (errorCode, data) => {
        const known = API_ERROR_TEXTS[errorCode]
        if (!known.withName) return text(known.key)
        const invalidName = readInvalidName(data)
        if (!invalidName) return text(`${known.key}Generic`)
        const name = errorCode === 'QUALIFICATION_RISK_FACTOR_UNKNOWN' ? riskFactorLabel(invalidName) : invalidName
        return text(known.key, {name})
    }

    const handleSubmitError = (err) => {
        if (err.name === "CanceledError") return
        console.log(err)
        setIsLoading(false)
        const errorCode = err.response?.data?.errorCode
        const known = API_ERROR_TEXTS[errorCode]
        if (known) {
            if (errorCode === 'INVALID_AI_SUGGESTION') {
                // The next "Créer" goes without the stale suggestion.
                setSuggestion(null)
                setAiFields(noAiFields())
            }
            setSubmitError(apiErrorText(errorCode, err.response?.data))
            setActiveTab(known.tab)
            return
        }
        toast.error(err.response?.data?.message || err.response?.data?.error)
    }

    const showResponseWarnings = (data) => {
        const warnings = Array.isArray(data?.warnings) ? data.warnings : []
        warnings.forEach((warning) => {
            const key = API_WARNING_TEXTS[warning?.code]
            if (key) toast.warning(text(key))
        })
    }

    // The emptied editor still holds « <p></p> »: fields count by their content.
    const missingFields = {
        name: !name.trim(),
        description: !toComparableText(description).trim(),
        pcs: !pcs.trim(),
        sector: !SECTOR_KEYS.includes(type),
    }
    const areRequiredFieldsFilled = () => !Object.values(missingFields).some(Boolean)
    const requiredError = (field) => missingFields[field] ? text('requiredField') : ''

    const handleSubmit = () => {
        if(areRequiredFieldsFilled()) {
            if (isRiskFactorMissing(risk)) {
                setSubmitError(text('errorRiskFactorRequired'))
                setActiveTab(TAB_RISK)
                return
            }
            setIsLoading(true)
            setSubmitError('')
            const payload = {
                token, name, description, pcs, rome, type, isToDisplayInMissionPreferences,
                tasks: buildTasksPayload(tasks),
                conditions: buildConditionsPayload(risk, riskyPositions.items),
                tools: buildNamesPayload(tools),
                licences: buildNamesPayload(licences),
            }
            if(objID) {
                ApiDatabase.updateQualification({...payload, idQualification: objID}, (data) => {
                    toast.success(text('updated'))
                    showResponseWarnings(data)
                    setIsLoading(false)
                    setRefreshQualification(data)
                    quitSlideOver()
                }, handleSubmitError)
            } else {
                const aiPayload = suggestion
                    ? {aiSuggestionId: suggestion.suggestionId, aiFields: listAiFields(aiFields)}
                    : {}
                ApiDatabase.postQualification({...payload, ...aiPayload}, (data) => {
                    toast.success(text('created'))
                    showResponseWarnings(data)
                    setIsLoading(false)
                    setRefreshQualifications(!refreshQualifications)
                    quitSlideOver()
                }, handleSubmitError)
            }
        } else {
            toast.error(text('missingFields'))
        }
    }

    const onEditorStateChange = (html) => {
        setDescription(html)
        if (!aiFields.description) return
        const aiDescription = aiDescriptionRef.current
        if (aiDescription.baseline === null && toComparableText(html) === toComparableText(aiDescription.html)) {
            aiDescription.baseline = html
            return
        }
        if (html !== aiDescription.baseline) markFieldEdited('description')
    }

    useEffect(() => {
        if(objID) {
            ApiDatabase.getQualificationByID({token, idQualification: objID}, (data) => {
                setName(data.name)
                setDescription(data.desc)
                setInitialDescription(data.desc)
                setPcs(data.codePCS)
                setRome(data.codeROME)
                setType(data.type)
                setIsToDisplayInMissionPreferences(data.displayInMissionPreferences)
                setIsActive(data.isActive)
                setTasks(toFormTasks(data.tasks))
                setRisk(toFormRisk(data.conditions))
                setTools(toFormItems(data.tools))
                setLicences(toFormItems(data.licences))
                storedValuesRef.current = toStoredValues(data)
                setRegeneratedFields({})
                setRegenerationSummary(null)
                setHasLoadedQualification(true)
            }, (err) => {
                // The form shows as before on a failed load, empty.
                setHasLoadedQualification(true)
                if (err.name !== "CanceledError") {
                    console.log(err)
                    toast.error(err.response.data.message)
                }
            })
        }
    }, [objID]);

    const quitSlideOver = () => {
        stopPolling()
        toggleSlideOver()
        setObjID('')
    }

    const restoreRequirements = () => {
        const snapshot = requirementsSnapshotRef.current
        if (!snapshot) return
        setRisk(snapshot.risk)
        setTools(snapshot.tools)
        setLicences(snapshot.licences)
        setAiFields((fields) => ({...fields, ...snapshot.aiFields}))
    }

    const showAiFailure = () => {
        stopPolling()
        restoreRequirements()
        setAiFailed(true)
        setStage(STAGE_FORM)
    }

    /** Risk factors come with the classification (a fixed table, no AI): applied as soon as they are known. */
    const applySuggestedConditions = (data, replacedFields) => {
        if (!replacedFields.includes('conditions') || !isConditionsValue(data?.conditions)) return
        setRisk(toSuggestedRisk(data.conditions))
        setAiFields((fields) => ({...fields, conditions: true}))
    }

    /** Fills the fields still "AI" (all of them the first time) and the untouched AI tasks. */
    const applySuggestion = (data, replacedFields, replacesTasks) => {
        const values = toSuggestedValues(data)
        const replaces = (field) => replacedFields.includes(field)
        if (replaces('name')) setName(values.name)
        if (replaces('description')) {
            setDescription(values.description)
            setInitialDescription(values.description)
            aiDescriptionRef.current = {html: values.description, baseline: null}
        }
        if (replaces('rome')) {
            setRome(values.rome)
            setRomeLabel(data.rome?.label ?? '')
        }
        if (replaces('pcs')) {
            setPcs(values.pcs)
            setPcsLabel(data.pcs?.label ?? '')
        }
        if (replaces('sector')) setType(values.sector)
        if (replacesTasks) setTasks((current) => mergeSuggestedTasks(current, data.tasks))
        applySuggestedConditions(data, replacedFields)
        if (replaces('tools')) setTools(toSuggestedItems(data.tools))
        if (replaces('licences')) setLicences(toSuggestedItems(data.licences))
        setAiFields((fields) => AI_FIELDS.reduce((next, field) => {
            if (!replaces(field)) return {...next, [field]: fields[field]}
            // Conditions already applied from `classified` stay AI even if `done` omits them.
            const isProposed = isFieldProposed(field, data) || (field === 'conditions' && fields.conditions)
            return {...next, [field]: isProposed}
        }, {}))
        setSuggestion({
            suggestionId: data.suggestionId,
            rome: data.rome,
            pcs: data.pcs,
            alternatives: {
                rome: data.alternatives?.rome ?? [],
                pcs: data.alternatives?.pcs ?? [],
            },
            similarQualifications: data.similarQualifications ?? [],
            alternativeName: data.alternativeName ?? null,
        })
        setAiFailed(false)
        setActiveTab(TAB_INFORMATIONS)
        setStage(STAGE_FORM)
    }

    /**
     * Asks the api for a suggestion, then polls it until done, failed, closed
     * or 4 minutes. Creation and modification only differ by what they do
     * with the answers.
     */
    const runSuggestion = (payload, {onClassified, onDone, onFailure}) => {
        stopPolling()
        const session = pollingRef.current.session
        const isCurrent = () => pollingRef.current.session === session

        setGenerationStatus(AI_STATUS.PENDING)
        setGenerationProgress(null)
        setAiFailed(false)
        setSubmitError('')
        // The form remounts after the generation screen: the editor restarts from the current text.
        setInitialDescription(description)
        setStage(STAGE_GENERATING)

        const failIfCurrent = () => {
            if (!isCurrent()) return
            stopPolling()
            onFailure()
        }

        pollingRef.current.deadlineTimer = setTimeout(failIfCurrent, AI_SUGGEST_CLIENT_TIMEOUT_MS)

        const poll = (suggestionId) => {
            pollingRef.current.pollTimer = setTimeout(() => {
                ApiDatabase.getQualificationAiSuggestion({suggestionId}, (data) => {
                    if (!isCurrent()) return
                    if (data?.status === AI_STATUS.DONE) {
                        stopPolling()
                        onDone(data)
                        return
                    }
                    if (data?.status === AI_STATUS.FAILED) {
                        failIfCurrent()
                        return
                    }
                    if (data?.status === AI_STATUS.CLASSIFIED) {
                        setGenerationStatus(AI_STATUS.CLASSIFIED)
                        setGenerationProgress(data)
                        if (onClassified) onClassified(data)
                    }
                    poll(suggestionId)
                }, failIfCurrent)
            }, AI_SUGGEST_POLL_INTERVAL_MS)
        }

        ApiDatabase.postQualificationAiSuggestion(payload, (data) => {
            if (!isCurrent()) return
            if (!data?.suggestionId) {
                failIfCurrent()
                return
            }
            poll(data.suggestionId)
        }, failIfCurrent)
    }

    const startSuggestion = (title, regenerate) => {
        const replacedFields = regenerate
            ? AI_FIELDS.filter((field) => !regenerate.keep.includes(field))
            : AI_FIELDS
        const replacesTasks = !regenerate || !regenerate.keep.includes('tasks')

        requirementsSnapshotRef.current = {
            risk,
            tools,
            licences,
            aiFields: REQUIREMENT_AI_FIELDS.reduce((picked, field) => ({...picked, [field]: aiFields[field]}), {}),
        }
        runSuggestion({title: title.trim(), ...(regenerate ? {regenerate} : {})}, {
            onClassified: (data) => applySuggestedConditions(data, replacedFields),
            onDone: (data) => applySuggestion(data, replacedFields, replacesTasks),
            onFailure: showAiFailure,
        })
    }

    /** Puts a value in the form; `isAi` marks it as the AI's, like at creation. */
    const applyFieldValue = (field, value, isAi) => {
        switch (field) {
            case 'description':
                setDescription(value)
                setInitialDescription(value)
                setDescriptionEditorKey((key) => key + 1)
                aiDescriptionRef.current = {html: isAi ? value : '', baseline: null}
                break
            case 'rome':
                setRome(value.code)
                setRomeLabel(value.label ?? '')
                break
            case 'pcs':
                setPcs(value.code)
                setPcsLabel(value.label ?? '')
                break
            case 'sector':
                setType(value)
                break
            case 'tasks':
                setTasks(value)
                break
            case 'conditions':
                setRisk(value)
                break
            case 'tools':
                setTools(value)
                break
            case 'licences':
                setLicences(value)
                break
            default:
                return
        }
        if (AI_FIELDS.includes(field)) setAiFields((fields) => ({...fields, [field]: isAi}))
    }

    const readRegeneratedFormValues = () => ({
        description: description ?? '',
        rome: {code: rome ?? '', label: romeLabel},
        pcs: {code: pcs ?? '', label: pcsLabel},
        sector: type ?? '',
        tasks,
        conditions: risk,
        tools,
        licences,
    })

    /**
     * A field the suggestion changes takes the suggested value. A field it
     * proposes as its baseline goes back to that baseline if a previous
     * regeneration had replaced it; a field it omits keeps its previous block.
     */
    const applyRegeneration = (data, baseline) => {
        const {proposed, changes} = diffRegeneration(baseline, data, SECTOR_KEYS, storedValuesRef.current.tasks)
        const nextFields = {}
        REGENERATED_FIELDS.forEach((field) => {
            if (changes[field]) {
                applyFieldValue(field, changes[field].after, true)
                nextFields[field] = changes[field]
                return
            }
            if (!regeneratedFields[field]) return
            if (field in proposed) applyFieldValue(field, baseline[field], false)
            else nextFields[field] = regeneratedFields[field]
        })
        setRegeneratedFields(nextFields)
        setRegenerationSummary(REGENERATED_FIELDS.filter((field) => nextFields[field]))
        setAiFailed(false)
        setActiveTab(TAB_INFORMATIONS)
        setStage(STAGE_FORM)
    }

    // Modification: the same suggestion as the creation, from the locked name.
    const regenerateFromStored = () => {
        const title = name.trim()
        if (!storedValuesRef.current || !title) return
        if (!isValidAiTitle(title)) {
            toast.error(aiText('titleLength', {min: AI_TITLE_MIN_LENGTH, max: AI_TITLE_MAX_LENGTH}))
            return
        }
        setAiTitle(title)
        // Read when the admin clicks: the form is hidden while the AI works.
        const baseline = toRegenerationBaseline(readRegeneratedFormValues(), regeneratedFields)
        runSuggestion({title}, {
            onDone: (data) => applyRegeneration(data, baseline),
            onFailure: () => {
                setAiFailed(true)
                setStage(STAGE_FORM)
            },
        })
    }

    const toggleRegeneratedField = (field) => {
        const change = regeneratedFields[field]
        if (!change) return
        const isRestored = !change.isRestored
        applyFieldValue(field, isRestored ? change.before : change.after, !isRestored)
        setRegeneratedFields((fields) => ({...fields, [field]: {...change, isRestored}}))
    }

    const handleStart = () => {
        const title = name.trim()
        if (!title) return
        if (isAiAvailable && useAi) {
            if (!isValidAiTitle(title)) {
                setAiTitleError(aiText('titleLength', {min: AI_TITLE_MIN_LENGTH, max: AI_TITLE_MAX_LENGTH}))
                return
            }
            setAiTitle(title)
            startSuggestion(title)
            return
        }
        setStage(STAGE_FORM)
    }

    // Cancelling a regeneration goes back to the form the admin already filled;
    // only the first generation has nothing to go back to.
    const cancelGeneration = () => {
        if (!isCreation) {
            stopPolling()
            setStage(STAGE_FORM)
            return
        }
        if (!suggestion) {
            quitSlideOver()
            return
        }
        stopPolling()
        restoreRequirements()
        setStage(STAGE_FORM)
    }

    // Creation: the title is the one typed now, not the one of the first run.
    const regenerationTitle = name.trim()
    const canRegenerate = isValidAiTitle(regenerationTitle) && currentStage !== STAGE_GENERATING && !isLoading

    const handleRegenerate = () => {
        if (!suggestion || !canRegenerate) return
        setAiTitle(regenerationTitle)
        startSuggestion(regenerationTitle, {
            suggestionId: suggestion.suggestionId,
            keep: buildRegenerateKeep(aiFields, tasks),
        })
    }

    const pickAlternative = (field) => (alternative) => {
        if (field === 'rome') {
            setRome(alternative.code)
            setRomeLabel(alternative.label ?? '')
        } else {
            setPcs(alternative.code)
            setPcsLabel(alternative.label ?? '')
        }
        // An alternative offered by the AI keeps the field "AI".
        setAiFields((fields) => ({...fields, [field]: true}))
    }

    // The AI's other name, once applied, counts as an AI value like the name it replaces.
    const applyAlternativeName = (alternativeName) => {
        setName(alternativeName)
        setAiFields((fields) => ({...fields, name: true}))
    }

    const changePositionAtRisk = (positionAtRisk) => {
        setRisk((current) => withPositionAtRisk(current, positionAtRisk))
        markFieldEdited('conditions')
    }

    const toggleFactor = (besttField) => {
        setRisk((current) => toggleRiskFactor(current, besttField))
        markFieldEdited('conditions')
    }

    const editRequirementList = (setList, field, edit) => (itemName) => {
        setList((items) => edit(items, itemName))
        markFieldEdited(field)
    }

    const delQualification = () => {
        if(objID) {
            ApiDatabase.delQualification({token, idQualification: objID}, (data) => {
                toast.success(text('deactivated'))
                setRefreshQualification(data)
                quitSlideOver()
            }, (err) => {
                if (err.name !== "CanceledError") {
                    console.log(err)
                    toast.error(err.response.data.message)
                }
            })
        }
    }

    const activeQualification = () => {
        if(objID) {
            ApiDatabase.activateQualification({token, idQualification: objID}, (data) => {
                toast.success(text('activated'))
                setRefreshQualification(data)
                quitSlideOver()
            }, (err) => {
                if (err.name !== "CanceledError") {
                    console.log(err)
                    toast.error(err.response.data.message)
                }
            })
        }
    }

    const tabs = [
        {id: TAB_INFORMATIONS, label: text('tabInformations')},
        {id: TAB_RISK, label: text('tabRisk')},
        {id: TAB_REQUIREMENTS, label: text('tabRequirements')},
        {
            id: TAB_TASKS,
            label: text('tabTasks'),
            count: tasks.length,
            // The count badge alone would read "Tâches 8"; the accessible name keeps the explicit form.
            ariaLabel: text('tabTasksWithCount', {count: tasks.length}),
        },
    ]

    const formatRegeneratedValue = (field, value) => {
        const orEmpty = (shown) => shown || regenerateText('emptyValue')
        const list = (names) => orEmpty(names.join(', '))
        switch (field) {
            case 'description':
                return orEmpty(toComparableText(value))
            case 'rome':
            case 'pcs':
                if (!value.code) return orEmpty('')
                return value.label ? aiText('alternative', {code: value.code, label: value.label}) : value.code
            case 'sector':
                return orEmpty(value ? sectorLabel(value) : '')
            case 'tasks':
                return list(value.map((task) => task.label))
            case 'conditions': {
                if (!value.positionAtRisk) return regenerateText('notAtRisk')
                const factors = Object.keys(value.penibilite).filter((factor) => value.penibilite[factor])
                return factors.length > 0 ? factors.map(riskFactorLabel).join(', ') : regenerateText('atRisk')
            }
            case 'tools':
            case 'licences':
                return list(value.map((item) => item.name))
            default:
                return ''
        }
    }

    const renderFieldChange = (field) => {
        const change = regeneratedFields[field]
        if (!change) return null
        return (
            <RegeneratedFieldChange
                fieldLabel={regeneratedFieldLabel(field)}
                before={formatRegeneratedValue(field, change.before)}
                after={formatRegeneratedValue(field, change.after)}
                isRestored={change.isRestored}
                isClamped={field === 'description'}
                onToggle={() => toggleRegeneratedField(field)}
            />
        )
    }

    const isLowConfidence = (field) => aiFields[field] && suggestion?.[field]?.confidence === 'low'

    const headerSubtitle = () => {
        if (isCreation && currentStage !== STAGE_FORM) return text('subtitle')
        return text(TAB_SUBTITLE_KEYS[activeTab] ?? TAB_SUBTITLE_KEYS[TAB_INFORMATIONS])
    }

    // A disabled fieldset disables the box without StyledInput's grey label background.
    const renderMissionPreferences = (isDisabled = false) => (
        <fieldset
            disabled={isDisabled}
            className={`min-w-0 rounded-lg border border-gray-200 p-4 select-none ${CHECKBOX_WRAPPER_CLASS} ${isDisabled ? 'opacity-50' : ''}`}
        >
            <StyledInput
                type="checkbox"
                checked={isToDisplayInMissionPreferences}
                onChange={() => setIsToDisplayInMissionPreferences(!isToDisplayInMissionPreferences)}
                label={
                    <div className={'flex flex-col gap-0.5'}>
                        <div className={'text-gray-900'}>{text('missionPreferences')}</div>
                        <div className={'text-gray-500'}>{text('missionPreferencesHelp')}</div>
                    </div>
                }
            />
        </fieldset>
    )

    const renderStartStage = () => (
        <div className={'flex flex-col gap-4'}>
            <WithHint hint={aiTitleError ? '' : text('nameWillLock')}>
                <StyledInput
                    type="text"
                    label={text('name')}
                    value={name}
                    error={aiTitleError}
                    onChange={e => {
                        setName(e.target.value)
                        setAiTitleError('')
                    }}
                />
            </WithHint>
            {isAiAvailable && (
                <div className={`flex flex-col gap-3 rounded-lg bg-yellow-50 p-4 select-none ${CHECKBOX_WRAPPER_CLASS}`}>
                    <StyledInput
                        type="checkbox"
                        checked={useAi}
                        onChange={() => {
                            setUseAi(!useAi)
                            setAiTitleError('')
                        }}
                        label={<span className={'font-medium text-gray-900'}>{aiText('useAi')}</span>}
                    />
                    {useAi && (
                        <div className={'flex flex-col gap-2'}>
                            <span className={'ft-sm text-gray-700'}>{aiText('useAiHelp')}</span>
                            <AiFieldPills/>
                        </div>
                    )}
                </div>
            )}
            {renderMissionPreferences()}
        </div>
    )

    const renderGeneratingStage = () => (
        <div className={'flex flex-col gap-4'}>
            <StyledInput
                type="text"
                label={text('name')}
                value={aiTitle}
                isDisabled
            />
            <AiGenerationSteps
                status={generationStatus}
                progress={generationProgress}
                sectorLabel={sectorLabel}
            />
            {renderMissionPreferences(true)}
        </div>
    )

    // Sized for four tabs on the panel width; scrolls rather than wraps if labels grow.
    const renderTabs = () => (
        <div role="tablist" className={'flex gap-1 mb-4 overflow-x-auto'}>
            {tabs.map((tab) => {
                const isSelected = activeTab === tab.id
                return (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        id={`qualification-tab-${tab.id}`}
                        aria-selected={isSelected}
                        aria-controls={`qualification-panel-${tab.id}`}
                        aria-label={tab.ariaLabel}
                        className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 ${
                            isSelected ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                        {tab.count !== undefined && (
                            <span className={'rounded bg-yellow-100 px-1.5 text-xs font-medium text-yellow-700'}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )

    const alternativeNameOffer = isCreation ? getAlternativeNameOffer(suggestion?.alternativeName, name) : ''

    const renderFormStage = () => (
        <>
            {aiFailed && <AiFailureMessage/>}
            {renderTabs()}
            {isCreation && suggestion && (
                <AiPrefilledBanner onRegenerate={handleRegenerate} disabled={!canRegenerate}/>
            )}
            {!isCreation && regenerationSummary && (
                <RegenerationSummary fieldLabels={regenerationSummary.map(regeneratedFieldLabel)}/>
            )}
            {submitError && (
                <div role="alert" className={'mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'}>
                    {submitError}
                </div>
            )}
            {/* Both panels stay mounted: the description editor would lose its content on a remount.
                The class must drop `flex` when hidden: `.flex` beats preflight's `[hidden]` rule (same specificity, declared later). */}
            <div
                role="tabpanel"
                id={`qualification-panel-${TAB_INFORMATIONS}`}
                aria-labelledby={`qualification-tab-${TAB_INFORMATIONS}`}
                hidden={activeTab !== TAB_INFORMATIONS}
                className={activeTab === TAB_INFORMATIONS ? 'flex flex-col gap-4' : 'hidden'}
            >
                {isCreation && suggestion?.similarQualifications?.length > 0 && (
                    <SimilarQualificationsAlert
                        qualifications={suggestion.similarQualifications}
                        sectorLabel={sectorLabel}
                    />
                )}
                <WithHint hint={missingFields.name ? '' : (objID ? text('nameLocked') : text('nameWillLock'))}>
                    <StyledInput
                        type="text"
                        label={<FieldLabel isAi={aiFields.name}>{text('name')}</FieldLabel>}
                        error={requiredError('name')}
                        value={name}
                        isDisabled={Boolean(objID)}
                        onChange={e => {
                            setName(e.target.value)
                            markFieldEdited('name')
                        }}
                    />
                    {alternativeNameOffer && (
                        <div data-testid="ai-alternative-name" className={'flex flex-wrap items-center gap-1 text-xs text-gray-500'}>
                            <span>{aiText('alternativeName', {name: alternativeNameOffer})}</span>
                            <button
                                type="button"
                                className={'text-gray-700 underline hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 rounded'}
                                onClick={() => applyAlternativeName(alternativeNameOffer)}
                            >
                                {aiText('useAlternativeName')}
                            </button>
                        </div>
                    )}
                </WithHint>
                <WithHint hint={missingFields.description ? '' : text('descriptionHint')}>
                    <label className={'flex flex-col gap-1 [&_.rich-text-editor]:rounded-md'}>
                        <div className={'ft-sm text-gray-500 flex gap-1'}>
                            <FieldLabel isAi={aiFields.description}>{text('description')}</FieldLabel>
                        </div>
                        <RichTextEditor
                            key={descriptionEditorKey}
                            initialContent={initialDescription}
                            onEditorStateChange={onEditorStateChange}
                        />
                    </label>
                    {/* Same message as StyledInput's `error`: the rich text editor has none of its own. */}
                    {missingFields.description && (
                        <div className={'text-red-500 flex items-center gap-1 text-xs'}>
                            <WarningCircle wh={16} color={'#EF4444'}/>
                            {requiredError('description')}
                        </div>
                    )}
                    {renderFieldChange('description')}
                </WithHint>
                <div className={'grid grid-cols-2 gap-3'}>
                    <WithHint hint={aiFields.rome && !regeneratedFields.rome ? romeLabel : ''}>
                        <StyledInput
                            type="text"
                            label={<FieldLabel isAi={aiFields.rome}>{text('rome')}</FieldLabel>}
                            isOptional
                            value={rome}
                            onChange={e => {
                                setRome(normalizeRomeInput(e.target.value))
                                setRomeLabel('')
                                markFieldEdited('rome')
                            }}
                        />
                        {renderFieldChange('rome')}
                    </WithHint>
                    <WithHint hint={aiFields.pcs && !missingFields.pcs && !regeneratedFields.pcs ? pcsLabel : ''}>
                        <StyledInput
                            type="text"
                            label={<FieldLabel isAi={aiFields.pcs}>{text('pcs')}</FieldLabel>}
                            error={requiredError('pcs')}
                            value={pcs}
                            onChange={e => {
                                setPcs(e.target.value)
                                setPcsLabel('')
                                markFieldEdited('pcs')
                            }}
                        />
                        {renderFieldChange('pcs')}
                    </WithHint>
                </div>
                {isCreation && isLowConfidence('rome') && (
                    <LowConfidenceAlert
                        field="rome"
                        alternatives={suggestion.alternatives.rome}
                        onPick={pickAlternative('rome')}
                    />
                )}
                {isCreation && isLowConfidence('pcs') && (
                    <LowConfidenceAlert
                        field="pcs"
                        alternatives={suggestion.alternatives.pcs}
                        onPick={pickAlternative('pcs')}
                    />
                )}
                <div className={'flex flex-col gap-1.5'}>
                    <StyledInput
                        type="select"
                        label={<FieldLabel isAi={aiFields.sector}>{text('sector')}</FieldLabel>}
                        list={SECTOR_OPTIONS}
                        error={requiredError('sector')}
                        value={type}
                        onChange={(e) => {
                            setType(e.target.value)
                            markFieldEdited('sector')
                        }}
                    />
                    {renderFieldChange('sector')}
                </div>
                {renderMissionPreferences()}
            </div>
            <div
                role="tabpanel"
                id={`qualification-panel-${TAB_RISK}`}
                aria-labelledby={`qualification-tab-${TAB_RISK}`}
                hidden={activeTab !== TAB_RISK}
            >
                <QualificationRiskTab
                    risk={risk}
                    riskyPositions={riskyPositions}
                    onPositionAtRiskChange={changePositionAtRisk}
                    onFactorToggle={toggleFactor}
                />
                {regeneratedFields.conditions && <div className={'mt-3'}>{renderFieldChange('conditions')}</div>}
            </div>
            <div
                role="tabpanel"
                id={`qualification-panel-${TAB_REQUIREMENTS}`}
                aria-labelledby={`qualification-tab-${TAB_REQUIREMENTS}`}
                hidden={activeTab !== TAB_REQUIREMENTS}
            >
                <QualificationRequirementsTab
                    tools={tools}
                    licences={licences}
                    toolOptions={toolOptions}
                    licenceOptions={licenceOptions}
                    onToolToggle={editRequirementList(setTools, 'tools', toggleRequirementItem)}
                    onToolRemove={editRequirementList(setTools, 'tools', removeRequirementItem)}
                    onLicenceToggle={editRequirementList(setLicences, 'licences', toggleRequirementItem)}
                    onLicenceRemove={editRequirementList(setLicences, 'licences', removeRequirementItem)}
                    toolsFooter={renderFieldChange('tools')}
                    licencesFooter={renderFieldChange('licences')}
                />
            </div>
            <div
                role="tabpanel"
                id={`qualification-panel-${TAB_TASKS}`}
                aria-labelledby={`qualification-tab-${TAB_TASKS}`}
                hidden={activeTab !== TAB_TASKS}
            >
                <QualificationTasksTab tasks={tasks} setTasks={setTasks}/>
                {regeneratedFields.tasks && <div className={'mt-3'}>{renderFieldChange('tasks')}</div>}
            </div>
        </>
    )

    const renderFooter = () => {
        if (isFirstLoadPending) {
            return (
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'SM'} color={'DISABLED'} disabled>{text('cancel')}</Button>
                    <Button size={'SM'} color={'DISABLED'} disabled>{text('confirm')}</Button>
                </div>
            )
        }
        if (isLoading) {
            return (
                <div className={'flex justify-between w-full'}>
                    <div className={'flex justify-end gap-2 w-full'}>
                        <Button size={'SM'} color={'DISABLED'}>{text('cancel')}</Button>
                        <Button size={'SM'} color={'DISABLED'}>{text('confirming')}</Button>
                    </div>
                </div>
            )
        }
        if (currentStage === STAGE_START) {
            const willGenerate = isAiAvailable && useAi
            const startLabel = willGenerate ? aiText('generate') : aiText('continue')
            return (
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'SM'} color={'SECONDARY'} onClick={quitSlideOver}>{text('cancel')}</Button>
                    <Button size={'SM'} color={name.trim() ? 'PRIMARY' : 'DISABLED'} disabled={!name.trim()} onClick={handleStart}>
                        {willGenerate && <SparklesIcon wh={20} color={name.trim() ? '#FFFFFF' : '#9CA3AF'}/>}
                        {startLabel}
                    </Button>
                </div>
            )
        }
        if (currentStage === STAGE_GENERATING) {
            return (
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'SM'} color={'SECONDARY'} onClick={cancelGeneration}>{text('cancel')}</Button>
                    {/* PRIMARYICO has no hover state, so the pale orange stays put while disabled. */}
                    <Button size={'SM'} color={'PRIMARYICO'} className={'opacity-50 cursor-not-allowed'} disabled>
                        <SparklesIcon wh={20} color={'#FFFFFF'}/>
                        {aiText('generatingButton')}
                    </Button>
                </div>
            )
        }
        return (
            <div className={'flex justify-between w-full'}>
                {objID && (
                    <div className={'flex gap-2'}>
                        {isActive && (
                            <div className={'flex'}>
                                <Button size={'SM'} color={'SECONDARY'} onClick={() => delQualification()}>
                                    <div className={'flex gap-2'}>
                                        <div className={'my-auto'}>
                                            <BanIcon wh={20} color={'#111827'}/>
                                        </div>
                                        <div className={'my-auto'}>
                                            {text('deactivate')}
                                        </div>
                                    </div>
                                </Button>
                            </div>
                        )}
                        {!isActive && (
                            <div className={'flex'}>
                                <Button size={'SM'} color={'DEFAULT'} onClick={() => activeQualification()}>
                                    <div className={'flex gap-2'}>
                                        <div className={'my-auto'}>
                                            <CheckIcon w={24} h={12} color={'#111827'}/>
                                        </div>
                                        <div className={'my-auto'}>
                                            {text('activate')}
                                        </div>
                                    </div>
                                </Button>
                            </div>
                        )}
                        {isAiAvailable && <RegenerateWithAiButton onClick={regenerateFromStored}/>}
                    </div>
                )}
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'SM'} color={'SECONDARY'} onClick={quitSlideOver}>{text('cancel')}</Button>
                    <Button
                        size={'SM'}
                        color={areRequiredFieldsFilled() && !isLoading ? 'PRIMARY' : 'DISABLED'}
                        disabled={!areRequiredFieldsFilled() || isLoading}
                        onClick={handleSubmit}
                    >
                        {objID ? text('confirm') : text('create')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <>
            <SlideOverHeader hide={quitSlideOver} hideShow={true} closeLabel={text('close')}>
                <span>{objID ? text('editTitle') : text('createTitle')}</span>
                {isFirstLoadPending ? (
                    <QualificationHeaderSkeleton/>
                ) : (
                    <div className={'text-gray-500 ft-sm font-normal mt-1'}>
                        {headerSubtitle()}
                    </div>
                )}
            </SlideOverHeader>
            <SlideOverBody>
                {isFirstLoadPending && <QualificationFormSkeleton/>}
                {!isFirstLoadPending && currentStage === STAGE_START && renderStartStage()}
                {!isFirstLoadPending && currentStage === STAGE_GENERATING && renderGeneratingStage()}
                {!isFirstLoadPending && currentStage === STAGE_FORM && renderFormStage()}
            </SlideOverBody>
            <SlideOverFooter>
                {renderFooter()}
            </SlideOverFooter>
        </>
    )
}

export default SlideOverQualification
